import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, User, Phone, ArrowLeft, Loader2, Stethoscope, Heart, Eye, EyeOff } from "lucide-react";
import PasswordStrength, { isPasswordValid } from "@/components/PasswordStrength";
import { NATIONALITIES } from "@/lib/nationalities";

const LOGO_URL = "/logo-infirmiers.png";

const Register = () => {
  const [searchParams] = useSearchParams();
  const [userType, setUserType] = useState(searchParams.get("type") || "patient");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [nationality, setNationality] = useState("");
  const [cguAccepted, setCguAccepted] = useState(false);
  const [rgpdConsent, setRgpdConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [registrationDone, setRegistrationDone] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cguAccepted || !rgpdConsent) {
      toast.error("Veuillez accepter les CGU et le consentement RGPD");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (!isPasswordValid(password)) {
      toast.error("Le mot de passe ne respecte pas les critères de sécurité");
      return;
    }

    if (!phone.trim()) {
      toast.error("Le numéro de téléphone est obligatoire");
      return;
    }

    if (userType === "patient" && !nationality) {
      toast.error("Veuillez sélectionner votre nationalité");
      return;
    }

    setLoading(true);

    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          name: fullName,
          first_name: firstName,
          last_name: lastName,
          phone: phone.trim(),
          nationality: userType === "patient" ? nationality : null,
          user_type: userType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Erreur lors de l'inscription");
      }

      if (data.email_verification_required) {
        setRegisteredEmail(email);
        setRegistrationDone(true);
      } else {
        login(data.user, data.token);
        toast.success("Inscription réussie !");
        if (userType === "nurse") {
          navigate("/nurse/onboarding");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: registeredEmail })
      });
      toast.success("Email de vérification renvoyé !");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setResending(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Pass user type in the redirect URL to preserve it after OAuth
    const redirectUrl = window.location.origin + `/callback?user_type=${userType}&role_explicit=1`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (registrationDone) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 mx-auto" />
            </Link>
          </div>
          <div className="card-dark p-8 text-center">
            <Mail className="w-16 h-16 text-[#00E073] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Vérifiez votre email</h2>
            <p className="text-[#94A3B8] mb-2">
              Un email de confirmation a été envoyé à :
            </p>
            <p className="text-white font-medium mb-4">{registeredEmail}</p>
            <p className="text-sm text-[#64748B] mb-6">
              Cliquez sur le lien dans l'email pour activer votre compte.
              Vérifiez vos spams si vous ne le trouvez pas.
            </p>
            <Button
              variant="outline"
              className="btn-outline w-full mb-3"
              onClick={handleResendVerification}
              disabled={resending}
              data-testid="resend-verification-btn"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Renvoyer l'email de vérification
            </Button>
            <Link to="/login">
              <Button className="btn-secondary w-full" data-testid="go-to-login-from-verify">
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 py-12 overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Logo centered and larger */}
          <Link to="/" className="block mb-8">
            <img src={LOGO_URL} alt="Infirmiers Privés" className="h-16 mx-auto" />
          </Link>
          
          <h1 className="text-3xl font-bold text-white mb-2 text-center">Créer un compte</h1>
          <p className="text-[#94A3B8] mb-6 text-center">
            Rejoignez la plateforme Infirmiers Privés
          </p>

          {/* User Type Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              type="button"
              onClick={() => setUserType("patient")}
              className={`p-4 rounded-xl border-2 transition-all ${
                userType === "patient"
                  ? "border-[#00E073] bg-[#00E073]/10"
                  : "border-[#2A3A4D] bg-[#1F2A38] hover:border-[#00E073]/50"
              }`}
              data-testid="patient-type-btn"
            >
              <Heart className={`w-8 h-8 mx-auto mb-2 ${userType === "patient" ? "text-[#00E073]" : "text-[#94A3B8]"}`} />
              <p className={`font-medium ${userType === "patient" ? "text-white" : "text-[#94A3B8]"}`}>Patient</p>
              <p className="text-xs text-[#64748B] mt-1">Je recherche un infirmier</p>
            </button>
            
            <button
              type="button"
              onClick={() => setUserType("nurse")}
              className={`p-4 rounded-xl border-2 transition-all ${
                userType === "nurse"
                  ? "border-[#00E073] bg-[#00E073]/10"
                  : "border-[#2A3A4D] bg-[#1F2A38] hover:border-[#00E073]/50"
              }`}
              data-testid="nurse-type-btn"
            >
              <Stethoscope className={`w-8 h-8 mx-auto mb-2 ${userType === "nurse" ? "text-[#00E073]" : "text-[#94A3B8]"}`} />
              <p className={`font-medium ${userType === "nurse" ? "text-white" : "text-[#94A3B8]"}`}>Infirmier</p>
              <p className="text-xs text-[#64748B] mt-1">Libéral, salarié ou retraité</p>
            </button>
          </div>

          {userType === "nurse" && (
            <div className="bg-[#1F2A38]/50 border border-[#2A3A4D] rounded-xl p-4 mb-6">
              <p className="text-sm text-[#CBD5E1]">
                <strong className="text-[#00E073]">Pas encore libéral ?</strong> Aucun problème ! 
                Nous vous accompagnons dans la création de votre structure et vos démarches administratives.
              </p>
            </div>
          )}

          {/* Google Register */}
          <Button 
            type="button"
            variant="outline"
            className="w-full mb-6 bg-white text-[#0D1C32] hover:bg-gray-100 border-0"
            onClick={handleGoogleRegister}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#2A3A4D]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0D1C32] text-[#64748B]">ou</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lastName" className="form-label">Nom</Label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input-dark pr-10"
                    placeholder="Dupont"
                    required
                    data-testid="lastname-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName" className="form-label">Prénom</Label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input-dark pr-10"
                    placeholder="Jean"
                    required
                    data-testid="firstname-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="form-label">Email</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark pr-10"
                  placeholder="votre@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="form-label">Téléphone</Label>
              <div className="relative">
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input-dark pr-10"
                  placeholder="06 12 34 56 78"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            {userType === "patient" && (
              <div>
                <Label htmlFor="nationality" className="form-label">Nationalité</Label>
                <select
                  id="nationality"
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  className="input-dark w-full"
                  required
                  data-testid="nationality-select"
                >
                  <option value="" disabled>Sélectionnez votre nationalité</option>
                  {NATIONALITIES.map((nat) => (
                    <option key={nat} value={nat}>{nat}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label htmlFor="password" className="form-label">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark pr-10"
                  placeholder="••••••••"
                  minLength={8}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <PasswordStrength password={password} />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="form-label">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`input-dark pr-10 ${confirmPassword && password !== confirmPassword ? 'border-red-500' : confirmPassword && password === confirmPassword ? 'border-emerald-500' : ''}`}
                  placeholder="••••••••"
                  minLength={8}
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
              )}
              {confirmPassword && password === confirmPassword && (
                <p className="text-xs text-emerald-400 mt-1">Les mots de passe correspondent</p>
              )}
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="cgu"
                  checked={cguAccepted}
                  onCheckedChange={(checked) => setCguAccepted(checked === true)}
                  className="mt-1"
                  data-testid="cgu-checkbox"
                />
                <label htmlFor="cgu" className="text-sm text-[#94A3B8] cursor-pointer select-none">
                  J'accepte les{" "}
                  <span 
                    className="text-[#00E073] hover:underline cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    role="link"
                    tabIndex={0}
                  >
                    Conditions Générales d'Utilisation
                  </span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="rgpd"
                  checked={rgpdConsent}
                  onCheckedChange={(checked) => setRgpdConsent(checked === true)}
                  className="mt-1"
                  data-testid="rgpd-checkbox"
                />
                <label htmlFor="rgpd" className="text-sm text-[#94A3B8] cursor-pointer select-none">
                  Je consens au traitement de mes données personnelles conformément à la{" "}
                  <span 
                    className="text-[#00E073] hover:underline cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    role="link"
                    tabIndex={0}
                  >
                    Politique de confidentialité
                  </span>
                </label>
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full mt-6" 
              disabled={loading || !cguAccepted || !rgpdConsent || !isPasswordValid(password) || password !== confirmPassword || (userType === "patient" && !nationality)}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Inscription...
                </>
              ) : (
                "Créer mon compte"
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#94A3B8]">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-[#00E073] hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1C32] to-transparent z-10"></div>
        <img 
          src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
          alt="Soin à domicile"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

export default Register;
